import {
  Component,
  Input,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  HostListener,
} from '@angular/core';
import Swal from 'sweetalert2';
import { QueryService } from '../../comun/services/query.service';
import { GlobalConstants } from '../../global-constants';
import { Subscription } from 'rxjs';
import { WebsocketService } from '../../comun/services/websocket.service';
import { LoaderComponent } from '../../comun/loader/loader.component';
import { LoaderService } from '../../comun/services/loader.service';

@Component({
  selector: 'app-header-user',
  templateUrl: './header-user.component.html',
  styleUrl: './header-user.component.css',
})
export class HeaderUserComponent implements OnDestroy, OnChanges {
  @Input() misdatos: any = {};
  @Input() busy: boolean = true;
  @Input() isScrolled: any = {};
  datos: any = { mensajes: 0, notificaciones: 0, solicitudes: 0, visitas_nuevas: 0 };
  private usuarioAvisosId = 0;
  private mensajesSub!: Subscription;
  global: any = GlobalConstants;
  private wsSubscription!: Subscription;
  menuOpen = false;
  avisosOpen = false;
  cerrandoSesion = false;
  avisosMensajes: Array<{ nombre: string; foto: string; remitenteId: number; recibidoEn: number; version: number }> = [];
  private avisoVersion = 0;
  private audioContext: AudioContext | null = null;

  get totalPendientes(): number {
    return (
      (Number(this.datos.mensajes) || 0) +
      (Number(this.datos.notificaciones) || 0) +
      (Number(this.datos.solicitudes) || 0) +
      (Number(this.datos.visitas_nuevas) || 0)
    );
  }

  toggleMenu(): void {
    this.avisosOpen = false;
    this.menuOpen = !this.menuOpen;
  }

  // Una notificación por remitente; las recientes aparecen primero.
  cerrarPopupMensaje(remitenteId: number): void {
    this.avisosMensajes = this.avisosMensajes.filter(a => a.remitenteId !== remitenteId);
  }

  private limpiarPopupsMensajes(): void {
    this.avisosMensajes = [];
  }

  trackByAvisoMensaje = (_indice: number, aviso: { remitenteId: number }): number => aviso.remitenteId;

  private mostrarPopupMensaje(evento: any): void {
    const remitenteId = Number(evento?.user_from);
    const conectadoId = Number(this.misdatos?.ID);
    if (!remitenteId || !conectadoId || remitenteId === conectadoId) return;

    const anterior = this.avisosMensajes.find(a => a.remitenteId === remitenteId);
    const version = ++this.avisoVersion;
    const nuevo = {
      remitenteId,
      nombre: anterior?.nombre || 'Alguien de iGuau',
      foto: anterior?.foto || '',
      recibidoEn: Date.now(),
      version
    };

    // Sustituye la notificación anterior de este remitente y la lleva arriba.
    this.avisosMensajes = [nuevo, ...this.avisosMensajes.filter(a => a.remitenteId !== remitenteId)]
      .sort((a, b) => b.recibidoEn - a.recibidoEn || b.version - a.version);
    this.reproducirAviso();

    // Solo consultar la ficha si aún no tenemos sus datos.
    if (anterior?.foto && anterior?.nombre && anterior.nombre !== 'Alguien de iGuau') return;
    this.query.query('misdatos', 'GET', { ID: remitenteId }).subscribe({
      next: (perfil: any) => {
        if (Number(this.misdatos?.ID) !== conectadoId) return;
        const actual = this.avisosMensajes.find(a => a.remitenteId === remitenteId);
        if (!actual) return; // El usuario cerró este aviso.
        const datos = Array.isArray(perfil) ? perfil[0] : perfil;
        this.avisosMensajes = this.avisosMensajes.map(a => a.remitenteId !== remitenteId ? a : {
          ...a,
          nombre: datos?.first_name || datos?.display_name || 'Alguien de iGuau',
          foto: datos?.foto?.foto_principal_thumb || ''
        });
      },
      error: () => { /* Mantener el aviso aunque falle la foto. */ }
    });
  }

  private reproducirAviso(): void {
    // Web Audio no requiere recursos externos; algunos navegadores exigen interacción previa.
    try {
      const Ctor = window.AudioContext;
      if (!Ctor) return;
      if (!this.audioContext) this.audioContext = new Ctor();
      const ctx = this.audioContext;
      if (ctx.state !== 'running') return;
      const ahora = ctx.currentTime;
      [660, 880].forEach((frecuencia, i) => {
        const osc = ctx.createOscillator();
        const volumen = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = frecuencia;
        volumen.gain.setValueAtTime(0.0001, ahora + i * 0.11);
        volumen.gain.exponentialRampToValueAtTime(0.09, ahora + i * 0.11 + 0.012);
        volumen.gain.exponentialRampToValueAtTime(0.0001, ahora + i * 0.11 + 0.15);
        osc.connect(volumen).connect(ctx.destination);
        osc.start(ahora + i * 0.11);
        osc.stop(ahora + i * 0.11 + 0.16);
      });
    } catch { /* Sonido opcional; nunca impedir que aparezca el aviso. */ }
  }

  toggleAvisos(): void {
    this.menuOpen = false;
    this.avisosOpen = !this.avisosOpen;
    if (this.avisosOpen) this.actualizarAvisos();
  }

  actualizarAvisos(): void {
    if (!this.misdatos?.ID) return;
    this.numeroMensajes();
    this.numeroNotificaciones();
    this.numeroSolicitudes();
    this.numeroVisitas();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('app-header-user')) {
      this.menuOpen = false;
      this.avisosOpen = false;
    }
  }

  // Desbloquear el audio con la primera interacción del usuario (política del navegador).
  @HostListener('document:pointerdown')
  @HostListener('document:keydown')
  habilitarSonido(): void {
    try {
      const Ctor = window.AudioContext;
      if (!Ctor) return;
      if (!this.audioContext) this.audioContext = new Ctor();
      if (this.audioContext.state === 'suspended') {
        void this.audioContext.resume().catch(() => {});
      }
    } catch { /* Sin soporte de audio: el popup seguirá funcionando. */ }
  }

  @HostListener('document:keydown.escape')
  closeMenu(): void {
    this.menuOpen = false;
    this.avisosOpen = false;
  }

  async logout(): Promise<void> {
    this.menuOpen = false;
    const result = await Swal.fire({
      title: 'Cerrar sesión',
      text: '¿Te desconectamos o te quedas otro ratito?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Me quedo',
      cancelButtonText: 'Sí, salir',
      confirmButtonColor: '#527B69',
    });
    if (result.isConfirmed || this.cerrandoSesion) return;
    this.loader.show();
    this.cerrandoSesion = true;
    // Mantener el orden original y completar el logout antes de redirigir.
    this.query.query('misdatos', 'ONLINE', { online: '' }).subscribe({
      next: () => this.finalizarChatYLogout(),
      error: () => this.finalizarChatYLogout(),
    });
  }

  private finalizarChatYLogout(): void {
    this.query.query('mensajes', 'CHATONLINE', { chatonline: '' }).subscribe({
      next: () => this.finalizarLogout(),
      error: () => this.finalizarLogout(),
    });
  }

  private finalizarLogout(): void {
    this.query.query('misdatos', 'LOGOUT', null).subscribe({
      next: () => (window.location.href = GlobalConstants.urlServer),
      error: () => {
        this.cerrandoSesion = false;
        Swal.fire(
          'Error',
          'No se ha podido cerrar la sesión. Inténtalo otra vez.',
          'error',
        );
      },
    });
  }

  constructor(
    private query: QueryService,
    private wsService: WebsocketService,
    private loader: LoaderService
  ) {}

  ngOnInit(): void {
    // Nos quedamos escuchando de forma persistente
    this.wsSubscription = this.wsService.escucharNotificaciones().subscribe({
      next: (data) => {
        if (
          (data.tipo == 'CHAT_NUEVOMENSAJE' &&
            data.user_to == this.misdatos.ID) ||
          (data.tipo == 'CHAT_LEERMENSAJES' &&
            data.user_from == this.misdatos.ID)
        ) {
          this.numeroMensajes();
          if (data.tipo === 'CHAT_NUEVOMENSAJE' && Number(data.user_to) === Number(this.misdatos.ID)) {
            this.mostrarPopupMensaje(data);
          }
        }
      },
      error: (err) => console.error(err),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['misdatos'] && this.misdatos?.ID && this.misdatos?.ID != 0) {
      const id = Number(this.misdatos.ID);
      if (id !== this.usuarioAvisosId) {
        this.limpiarPopupsMensajes();
        this.usuarioAvisosId = id;
        this.datos = { mensajes: 0, notificaciones: 0, solicitudes: 0, visitas_nuevas: 0 };
      }
      this.actualizarAvisos();
    }
  }

  goOpcion(url: string) {
    window.location.href = GlobalConstants.urlServer + url;
  }

  isActive(url: string): boolean {
    const currentPath =
      window.location.protocol +
      '//' +
      window.location.hostname +
      window.location.pathname;
    if (url === '') return GlobalConstants.urlServer + url === currentPath;
    else return currentPath.includes(GlobalConstants.urlServer + url);
  }

  numeroMensajes() {
    this.query
      .query('mensajes', 'NUMEROMENSAJESSINLEER', null)
      .subscribe((resultado: any) => {
        this.datos.mensajes = Math.max(0, Number(resultado?.numero) || 0);
      });
  }

  numeroSolicitudes(): void {
    const id = Number(this.misdatos?.ID);
    if (!id) return;
    this.query.query('seguir', 'NUMERO', { se_us_id_to: id, se_estado: 'pendiente' })
      .subscribe({
        next: (resultado: any) => {
          if (Number(this.misdatos?.ID) === id) this.datos.solicitudes = Math.max(0, Number(resultado?.numero) || 0);
        },
        error: (error: any) => console.error('Error consultando solicitudes', error)
      });
  }

  numeroVisitas(): void {
    const id = Number(this.misdatos?.ID);
    if (!id) return;
    this.query.query('visita', 'NUMERO', { ID: id })
      .subscribe({
        next: (resultado: any) => {
          if (Number(this.misdatos?.ID) === id) this.datos.visitas_nuevas = Math.max(0, Number(resultado?.nuevas) || 0);
        },
        error: (error: any) => console.error('Error consultando visitas', error)
      });
  }

  numeroNotificaciones() {
    this.datos.notificaciones = Number(0);
    // Obtener los mensajes en firebase
    /*this.mensajesSub = this.chatService.obtenerMensajesParaMi(this.misdatos.ID.toString(), 'COMPARTIR_PERFIL')
      .subscribe(mensajes => {
        // Esto se ejecuta cada vez que recibes un mensaje
        this.datos.notificaciones = Number(mensajes.length);
        //if (this.datos.mensajes > 0)
        //  this.toastr.info('Nuevo mensaje');
      });*/
  }

  ngOnDestroy(): void {
    this.limpiarPopupsMensajes();
    if (this.audioContext) {
      void this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    if (this.mensajesSub) {
      this.mensajesSub.unsubscribe();
    }
    // Buenas prácticas: nos desuscribimos al destruir el componente para evitar fugas de memoria
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
    }
  }
}
